export default function initMenu() {
  Alpine.data('menu', () => ({
    navActive: false,
    searchActive: false,
    submenuActive: false,
    isDesktop: false,
    desktopMq: null as MediaQueryList | null,

    init() {
      this.desktopMq = window.matchMedia('(min-width: 64rem)');
      this.syncIsDesktop();

      this.$watch('navActive', () => {
        this.$root.classList.toggle('nav-active', this.navActive);
      });
      this.$watch('searchActive', () => {
        this.$root.classList.toggle('search-active', this.searchActive);
        this.updateFocusScope();
      });
      this.$watch('submenuActive', () => this.updateFocusScope());
      this.$watch('navActive', () => {
        this.updateSubmenuState();
        this.updateFocusScope();
      });

      this.desktopMq.addEventListener('change', () => {
        const wasDesktop = this.isDesktop;
        this.syncIsDesktop();

        if (!wasDesktop && this.isDesktop && this.navActive) {
          this.navActive = false;
          this.resetExpandedSubmenus();
        }

        this.updateSubmenuState();
        this.updateFocusScope();
      });

      window.addEventListener('keydown', (e) => {
        this.handleSearchFocusTrapKeydown(e);

        if (e.key === 'Escape') {
          if (this.searchActive) {
            this.closeSearch();
          }
          if (this.navActive) {
            this.syncIsDesktop();
            if (this.isDesktop) {
              this.navActive = false;
              this.close();
            } else {
              this.mobileClose();
            }
          }
        }
      });

      this.updateSubmenuState();
      this.updateFocusScope();
    },

    handleSearchFocusTrapKeydown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !this.isDesktop || !this.searchActive) {
        return;
      }

      const focusableElements = this.getSearchTrapElements();

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!(activeElement instanceof HTMLElement) || !focusableElements.includes(activeElement)) {
        e.preventDefault();
        firstElement.focus();
        return;
      }

      if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
        return;
      }

      if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    },

    updateSubmenuState() {
      this.submenuActive = this.navActive && document.querySelector('.group\\/link-nav.active-drop') !== null;
    },

    syncIsDesktop() {
      this.isDesktop = this.desktopMq?.matches ?? false;
    },

    getSearchTrapElements() {
      const headerLogoLink = this.$refs.headerLogoLink as HTMLElement | undefined;
      const searchInput = this.$refs.searchInput as HTMLElement | undefined;
      const submenuSearch = this.$refs.submenuSearch as HTMLElement | undefined;
      const accessibilityButtonsContainer = this.$refs.accessibilityButtons as HTMLElement | undefined;
      const searchButtons = submenuSearch
        ? Array.from(submenuSearch.querySelectorAll('button')) as HTMLElement[]
        : [];
      const accessibilityButtons = accessibilityButtonsContainer
        ? Array.from(accessibilityButtonsContainer.querySelectorAll('button')) as HTMLElement[]
        : [];

      return [headerLogoLink, searchInput, ...searchButtons, ...accessibilityButtons].filter((el): el is HTMLElement => {
        if (!(el instanceof HTMLElement)) {
          return false;
        }

        return !el.inert && !el.hasAttribute('disabled') && el.tabIndex !== -1;
      });
    },

    focusDesktopSearchTrigger() {
      if (!this.isDesktop) {
        return;
      }

      const desktopSearchTrigger = this.$refs.desktopSearchTrigger as HTMLElement | undefined;
      this.focusAfterTick(desktopSearchTrigger, 20);
    },

    focusAfterTick(el: HTMLElement | null | undefined, delayMs = 0) {
      if (!(el instanceof HTMLElement)) {
        return;
      }

      this.$nextTick(() => {
        window.setTimeout(() => {
          if (el.inert) {
            return;
          }

          el.focus();
        }, delayMs);
      });
    },

    focusFirstMainMenuItem() {
      const primaryMenu = this.$refs.primaryMenu as HTMLElement | undefined;
      const firstItem = primaryMenu?.querySelector('li') as HTMLElement | null;
      const firstControl = firstItem?.querySelector('button, a') as HTMLElement | null;
      this.focusAfterTick(firstControl);
    },

    focusFirstSubmenuLink(navItem: HTMLElement | null | undefined) {
      if (!(navItem instanceof HTMLElement)) {
        return;
      }

      const submenuPanel = navItem.querySelector('.menu-drop') as HTMLElement | null;
      const submenuContent = submenuPanel?.querySelector('[id^="sub-menu-"]') as HTMLElement | null;
      const firstSubmenuLink = submenuContent?.querySelector('a') as HTMLElement | null;
      // Wait for the submenu panel classes to apply before moving focus.
      this.focusAfterTick(firstSubmenuLink, 250);
    },

    setInert(el: Element | undefined, state: boolean) {
      if (!el || !(el instanceof HTMLElement)) {
        return;
      }

      el.inert = state;
    },

    setFirstLevelControlsFocusable(state: boolean) {
      const navItems = document.querySelectorAll('.group\\/link-nav');

      navItems.forEach((item) => {
        if (!(item instanceof HTMLElement)) {
          return;
        }

        const isActiveItem = item.classList.contains('active-drop');
        item.inert = !state && !isActiveItem;

        const directControl = item.querySelector(':scope > button, :scope > a');
        if (!(directControl instanceof HTMLElement)) {
          return;
        }

        // Keep submenu content reachable while removing first-level controls from tab order.
        directControl.inert = !state;
      });
    },

    setSubmenuPanelsFocusable(state: boolean) {
      const panels = document.querySelectorAll('.menu-drop');

      panels.forEach((panel) => {
        if (!(panel instanceof HTMLElement)) {
          return;
        }

        const parentItem = panel.closest('.group\\/link-nav');
        const isActivePanel = parentItem instanceof HTMLElement && parentItem.classList.contains('active-drop');
        panel.inert = !state && !isActivePanel;
      });
    },

    resetExpandedSubmenus() {
      const navItems = document.getElementsByClassName('group/link-nav');
      Array.from(navItems).forEach((item) => {
        item.classList.remove('active-drop');

        const menuDrop = item.querySelector('button') as HTMLElement | null;
        if (menuDrop) {
          menuDrop.ariaExpanded = 'false';
        }
      });
    },

    updateFocusScope() {
      const lockDesktopMenuDuringSearch = this.isDesktop && this.searchActive;
      const lockHeaderControls = !this.isDesktop && this.navActive;
      const lockMainNavControls = !this.isDesktop && this.navActive && this.submenuActive;
      const lockMainContent =
        (!this.isDesktop && this.navActive)
        || (this.isDesktop && this.submenuActive)
        || (this.isDesktop && this.searchActive);

      this.setFirstLevelControlsFocusable(!lockMainNavControls);
      this.setSubmenuPanelsFocusable(!lockMainNavControls);
      this.setInert(this.$refs.submenuSearch as Element | undefined, lockMainNavControls);
      this.setInert(this.$refs.mobileLanguageSwitch as Element | undefined, lockMainNavControls);
      this.setInert(this.$refs.mobileOpenMenuButton as Element | undefined, lockHeaderControls);
      this.setInert(this.$refs.headerLogoLink as Element | undefined, lockHeaderControls);
      this.setInert(this.$refs.primaryMenu as Element | undefined, lockDesktopMenuDuringSearch);
      this.setInert(this.$refs.desktopUtilities as Element | undefined, lockDesktopMenuDuringSearch);
      this.setInert(document.getElementById('main') as Element | undefined, lockMainContent);
    },

    open() {
      this.syncIsDesktop();
      let activeNavItem: HTMLElement | null = null;
      // @ts-ignore
      const event = this.$event;
      if (this.isDesktop || event instanceof PointerEvent) {
        this.navActive = true;
        const navItems = document.getElementsByClassName('group/link-nav');
        Array.from(navItems).forEach((item) => {
          item.classList.remove('active-drop');
        });
        if (this.$el.classList.contains('group/link-nav')) {
          this.$el.classList.add('active-drop');
          activeNavItem = this.$el as HTMLElement;
          const menuDrop = this.$el.querySelector('button') as HTMLElement;
          menuDrop.ariaExpanded = 'true';
        } else {
          activeNavItem = this.$el.closest('.group\\/link-nav') as HTMLElement | null;
          activeNavItem?.classList.add('active-drop');
          this.$el.ariaExpanded = 'true';
        }
      }
      this.updateSubmenuState();
      this.updateFocusScope();

      if (!this.isDesktop && this.submenuActive) {
        this.focusFirstSubmenuLink(activeNavItem);
      }
    },

    close() {
      this.syncIsDesktop();
      if (this.isDesktop) {
        this.navActive = false;
        this.resetExpandedSubmenus();
        if (this.$el.classList.contains('group/link-nav')) {
          const menuDrop = this.$el.querySelector('button') as HTMLElement;
          menuDrop.ariaExpanded = 'false';
        } else {
          this.$el.ariaExpanded = 'false';
        }
      }
      this.updateSubmenuState();
      this.updateFocusScope();
    },

    toggle() {
      // @ts-ignore
      const event = this.$event as KeyboardEvent;
      let openedSubmenuItem: HTMLElement | null = null;
      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        event.stopPropagation();

        if (!this.navActive) {
          this.open();
        } else {
          if (this.$el.parentElement?.classList.contains('active-drop')) {
            this.close();
          } else {
            const navItems = document.getElementsByClassName('group/link-nav');
            Array.from(navItems).forEach((item) => {
              item.classList.remove('active-drop');
              const menuDrop = item.querySelector('button') as HTMLElement|null;
              if (menuDrop) {
                menuDrop.ariaExpanded = 'false';
              }
            });
            this.$el.parentElement?.classList.add('active-drop');
            this.$el.ariaExpanded = 'true';
            openedSubmenuItem = this.$el.parentElement as HTMLElement | null;
          }
        }
      }

      this.updateSubmenuState();
      this.updateFocusScope();

      if (!this.isDesktop && openedSubmenuItem && this.submenuActive) {
        this.focusFirstSubmenuLink(openedSubmenuItem);
      }
    },

    mobileOpen() {
      this.navActive = true;
      this.$el.ariaExpanded = 'true';
      this.updateSubmenuState();
      this.updateFocusScope();
      this.focusFirstMainMenuItem();
    },

    mobileClose() {
      this.navActive = false;
      this.mobileBack();
      this.updateSubmenuState();
      this.updateFocusScope();
      this.focusAfterTick(this.$refs.mobileOpenMenuButton as HTMLElement | undefined);
    },

    mobileBack() {
      this.resetExpandedSubmenus();

      this.updateSubmenuState();
      this.updateFocusScope();

      if (!this.isDesktop && this.navActive) {
        this.focusFirstMainMenuItem();
      }
    },

    openSearch() {
      // @ts-ignore
      const event = this.$event as KeyboardEvent|PointerEvent;
      if (
        (event instanceof KeyboardEvent && (event.code === 'Enter' || event.code === 'Space')) ||
        event instanceof PointerEvent
      ) {
        this.searchActive = true;
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.searchInput?.focus();
          }, 20); // We have to wait for the DOM to be updated so that the searchInput is focusable
        });
      }
    },

    closeSearch() {
      // @ts-ignore
      const event = this.$event as KeyboardEvent|PointerEvent|undefined;
      if (event instanceof KeyboardEvent && event.code !== 'Enter' && event.code !== 'Space' && event.key !== 'Escape') {
        return;
      }

      if (event === undefined || event instanceof KeyboardEvent || event instanceof PointerEvent) {
        this.searchActive = false;
        this.focusDesktopSearchTrigger();
      }
    },
  }))
}
